import {Box, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent} from "@mui/material";
import {useMemo} from "react";

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const MonthYearPicker = ({
  value,
  minDate,
  maxDate,
  onChange
} : {
  value: Date,
  minDate?: Date,
  maxDate?: Date,
  onChange: (value: Date) => void
}) => {
    const currentMonth = value.getMonth();
    const currentYear = value.getFullYear();

    // Calculate available year range
    const {minYear, maxYear} = useMemo(() => {
        const min = minDate ? minDate.getFullYear() : currentYear - 10;
        const max = maxDate ? maxDate.getFullYear() : currentYear + 10;
        return {minYear: min, maxYear: max};
    }, [minDate, maxDate, currentYear]);

    // Generate year options
    const yearOptions = useMemo(() => {
        const years = [];
        for (let year = minYear; year <= maxYear; year++) {
            years.push(year);
        }
        return years;
    }, [minYear, maxYear]);

    // Check if a month is disabled based on min/max dates
    const isMonthDisabled = (monthIndex: number): boolean => {
        if (minDate) {
            const minYear = minDate.getFullYear();
            const minMonth = minDate.getMonth();
            if (currentYear === minYear && monthIndex < minMonth) {
                return true;
            }
        }
        if (maxDate) {
            const maxYear = maxDate.getFullYear();
            const maxMonth = maxDate.getMonth();
            if (currentYear === maxYear && monthIndex > maxMonth) {
                return true;
            }
        }
        return false;
    };

    const handleMonthChange = (event: SelectChangeEvent<number>) => {
        const newMonth = event.target.value as number;
        const newDate = new Date(currentYear, newMonth, 1);
        onChange(newDate);
    };

    const handleYearChange = (event: SelectChangeEvent<number>) => {
        const newYear = event.target.value as number;
        let newMonth = currentMonth;
        
        // Adjust month if it's out of range for the new year
        if (minDate && newYear === minDate.getFullYear()) {
            newMonth = Math.max(newMonth, minDate.getMonth());
        }
        if (maxDate && newYear === maxDate.getFullYear()) {
            newMonth = Math.min(newMonth, maxDate.getMonth());
        }
        
        const newDate = new Date(newYear, newMonth, 1);
        onChange(newDate);
    };

    return (
        <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl sx={{ minWidth: 150 }}>
              <InputLabel id="month-select-label" variant="standard">Start Month</InputLabel>
                <Select
                    labelId="month-select-label"
                    id="month-select"
                    variant="standard"
                    size="small"
                    margin="dense"
                    value={currentMonth}
                    label="Month"
                    onChange={handleMonthChange}
                >
                    {MONTHS.map((month, index) => (
                        <MenuItem 
                            key={index} 
                            value={index}
                            disabled={isMonthDisabled(index)}
                        >
                            {month}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl sx={{ minWidth: 80 }}>
              <InputLabel id="year-select-label" variant="standard">Start Year</InputLabel>
                <Select
                    labelId="year-select-label"
                    id="year-select"
                    variant="standard"
                    size="small"
                    margin="dense"
                    value={currentYear}
                    label="Year"
                    onChange={handleYearChange}
                >
                    {yearOptions.map((year) => (
                        <MenuItem key={year} value={year}>
                            {year}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    )
}

export default MonthYearPicker